import { useGetResidenceStatusList } from "@/api/services/global/master";
import { Checkbox } from "@/components/elements/Checkbox";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Select } from "@/components/elements/Select";
import { YearMonthAmountInput } from "@/features/talent/components/talent-form/YearMonthAmountInput";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { calculateSpecificSkillTransitionPeriod } from "@/features/talent/utils/calculateSpecificSkillTransitionPeriod";
import { divider } from "@/theme/colors";
import { BEFORE_ENTRY_LABEL } from "@/features/talent/constants/form";
import { Grid, Stack, Collapse, Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

/** Max remaining period for SSW1 (5 years) */
const BEFORE_ENTRY_REMAINING_YEAR = 5;
const BEFORE_ENTRY_REMAINING_MONTH = 0;

type TalentFormResidenceStatusProps = {
  /** When false, render plain Typography header + content (no bordered InnerBox, no collapse).
   *  Default true keeps existing edit-talent flow unchanged. */
  bordered?: boolean;
};

export const TalentFormResidenceStatus = ({ bordered = true }: TalentFormResidenceStatusProps) => {
  const methods = useFormContext<TTalentForm>();
  const { control, setValue, trigger } = methods;
  const { residenceStatusOptions } = useGetResidenceStatusList();

  const specifiedSkillFirstStartDate = useWatch({ name: "specifiedSkillFirstStartDate" });
  const blankPeriodMonthly = useWatch({ name: "blankPeriodMonthly" });
  const isBeforeEntry = useWatch({ name: "isBeforeEntry" });

  const [componentKey, setComponentKey] = useState(0);

  useEffect(() => {
    // When "未入国・初回申請前" is checked, set remaining to 5年0ヶ月
    if (isBeforeEntry) {
      setValue("specifiedSkillFirstRemainingTerm", `${BEFORE_ENTRY_REMAINING_YEAR}/${BEFORE_ENTRY_REMAINING_MONTH}`);
      setValue("specifiedSkillFirstRemainingTermYear", BEFORE_ENTRY_REMAINING_YEAR);
      setValue("specifiedSkillFirstRemainingTermMonth", BEFORE_ENTRY_REMAINING_MONTH);
      setComponentKey((prev) => prev + 1);
      return;
    }

    if (!specifiedSkillFirstStartDate) {
      setValue("specifiedSkillFirstRemainingTerm", "");
      setValue("specifiedSkillFirstRemainingTermYear", 0);
      setValue("specifiedSkillFirstRemainingTermMonth", 0);
      setComponentKey((prev) => prev + 1);
      return;
    }

    const { monthPeriod, yearPeriod } = calculateSpecificSkillTransitionPeriod({
      specifiedSkillFirstStartDate: specifiedSkillFirstStartDate,
      blankPeriodMonthly: blankPeriodMonthly?.toString() || "0",
    });

    setValue("specifiedSkillFirstRemainingTerm", `${yearPeriod}/${monthPeriod}`);
    setValue("specifiedSkillFirstRemainingTermYear", yearPeriod);
    setValue("specifiedSkillFirstRemainingTermMonth", monthPeriod);

    // Trigger validation
    trigger("specifiedSkillFirstRemainingTerm");

    setComponentKey((prev) => prev + 1);
  }, [specifiedSkillFirstStartDate, blankPeriodMonthly, isBeforeEntry, setValue, trigger]);

  /** Handle checkbox toggle — clear disabled fields when checked */
  const handleBeforeEntryChange = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      setValue("specifiedSkillFirstStartDate", null);
      setValue("blankPeriodMonthly", null);
    }
  };

  const { expanded, toggle } = useCollapsible();

  // Inner field block — identical layout for both bordered and borderless variants.
  const fields = (
    <>
      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="residenceStatusId" currentValueType="select" currentOptionsKey="residenceStatus">
              <Select placeholder="選択してください" options={residenceStatusOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="periodOfStay" currentValueType="date">
              <InputDatePicker />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="cardNumber" currentValueType="text">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <FormField control={control} name="specifiedSkillFirstStartDate" currentValueType="date">
              <InputDatePicker disabled={!!isBeforeEntry} />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="blankPeriodMonthly" currentValueType="text">
              <Input disabled={!!isBeforeEntry} />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="specifiedSkillFirstRemainingTerm" currentValueType="text">
              <YearMonthAmountInput
                key={componentKey}
                readonly={true}
                isError={false}
                nameYear="specifiedSkillFirstRemainingTermYear"
                nameMonth="specifiedSkillFirstRemainingTermMonth"
              />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      {/* 未入国・初回申請前 checkbox */}
      <Stack mt={1}>
        <Box>
          <FormField control={control} name="isBeforeEntry" noLabel>
            <Checkbox label={BEFORE_ENTRY_LABEL} checked={!!isBeforeEntry} onChange={handleBeforeEntryChange} />
          </FormField>
        </Box>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="passportNumber" currentValueType="text">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="passportExpiryDate" currentValueType="date">
              <InputDatePicker />
            </FormField>
          </Grid>
        </Grid>
      </Stack>
    </>
  );

  // Borderless variant: plain section header + inline fields.
  // Used in scan-talent tabs (IROHANA_MATCH_DEV-280) where the modal already
  // owns the surrounding chrome.
  if (!bordered) {
    return (
      <Stack mt={3}>
        <Typography variant="sub16Bold">在留資格情報</Typography>
        {fields}
      </Stack>
    );
  }

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <CollapsibleSectionHeader title="在留資格情報" expanded={expanded} onToggle={toggle} />
      <Collapse in={expanded}>{fields}</Collapse>
    </InnerBox>
  );
};
